/**
 * HUD rendering: health bars, turn info, player bars, game log.
 * DOM manipulation, reads GameState.
 */

import type { GameState, GameMode, ModeConfig, PlayerState, Vec3, WinResult, ShotRecord } from '../types';
import type { ResolutionPace } from '../types';
import { PACE_LABELS } from '../core/resolutionDirector';
import { shotOfTheMatch, summarizeShots, describeShot } from '../core/shotRecord';
import { getModeRules, matchDisplayName } from '../core/modeRules';
import { TEAMS, MODE_RULES, WEAPONS, PHYS_DT } from '../constants';
import { theme } from './theme';
import { fx, isFlavorEnabled, updateFlavorToggles } from './flavor';
import { isDevMode, updateDevToggles } from './devMode';

// =============================================================================
// PLAYER BARS
// =============================================================================

export function buildPlayerBars(players: PlayerState[]): void {
  // Update log highlight table with current player names/colors
  setPlayerHighlights(players);

  const row = document.getElementById('players-row');
  if (!row) return;

  row.innerHTML = '';

  // Group players by teamIndex
  const teamMap = new Map<number, PlayerState[]>();
  for (const p of players) {
    const arr = teamMap.get(p.teamIndex) || [];
    arr.push(p);
    teamMap.set(p.teamIndex, arr);
  }

  const isMultiUnit = players.some(p => p.unitIndex > 0);
  const UNIT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

  for (const [teamIdx, teamPlayers] of teamMap) {
    const team = TEAMS[teamIdx % TEAMS.length];
    const color = team.primary;

    const groupEl = document.createElement('div');
    groupEl.className = 'team-group';
    groupEl.id = `team-group-${teamIdx}`;
    groupEl.style.borderColor = `${color}30`;

    // Team header
    const header = document.createElement('div');
    header.className = 'team-group-header';
    header.style.color = color;
    header.textContent = teamPlayers[0].companyName.toUpperCase();
    groupEl.appendChild(header);

    // Unit rows
    for (const p of teamPlayers) {
      const d = document.createElement('div');
      d.className = 'player-bar';
      d.id = `pbar-${p.id}`;
      d.style.background = `${color}10`;

      // Unit label (A/B/C) or bot icon for single-unit
      const label = isMultiUnit
        ? `${UNIT_LETTERS[p.unitIndex] ?? p.unitIndex}`
        : (p.isBot ? '●' : '●');

      // Heal item count
      const healCount = ((p.loadout.items?.healSmall ?? 0) + (p.loadout.items?.healLarge ?? 0));
      const healBadge = healCount > 0
        ? `<span class="heal-text" id="pheal-${p.id}" style="color:${theme.heal}; font-size:11px; font-weight:700;">✚${healCount}</span>`
        : `<span class="heal-text" id="pheal-${p.id}" style="display:none;"></span>`;

      d.innerHTML = `
        <span class="unit-label" style="color:${color};">${label}</span>
        <div class="health-bar-outer">
          <div class="health-bar-inner" id="phbar-${p.id}" style="width:${Math.max(0, p.health)}%;background:${color};"></div>
        </div>
        <span class="hp-text" id="phtext-${p.id}">${Math.max(0, Math.round(p.health))}</span>
        <span class="cbp-text" id="pcbp-${p.id}">⬡${p.cbp}</span>
        ${healBadge}
        <span class="vis-tag" id="pvis-${p.id}"></span>
      `;
      groupEl.appendChild(d);
    }

    row.appendChild(groupEl);
  }
}

export function updatePlayerBars(state: GameState): void {
  for (const p of state.players) {
    const hbar = document.getElementById(`phbar-${p.id}`);
    const htext = document.getElementById(`phtext-${p.id}`);
    const pbar = document.getElementById(`pbar-${p.id}`);

    if (hbar) {
      hbar.style.width = `${Math.max(0, p.health)}%`;
    }
    if (htext) {
      htext.textContent = String(Math.max(0, Math.round(p.health)));
    }
    const cbpEl = document.getElementById(`pcbp-${p.id}`);
    if (cbpEl) {
      cbpEl.textContent = `⬡${p.cbp}`;
    }
    const healEl = document.getElementById(`pheal-${p.id}`);
    if (healEl) {
      const healCount = ((p.loadout.items?.healSmall ?? 0) + (p.loadout.items?.healLarge ?? 0));
      if (healCount > 0) {
        healEl.textContent = `✚${healCount}`;
        healEl.style.display = '';
      } else {
        healEl.style.display = 'none';
      }
    }
    if (pbar) {
      pbar.style.opacity = p.alive ? '1' : '0.3';
      pbar.classList.toggle('active', p.id === state.currentPlayerIndex);
    }
  }
}

/**
 * Enemy chips say what the viewer knows: SEEN (in line of sight now), the age of
 * the last sighting (T-n, from the viewer's ghost markers), or nothing yet ("?").
 * Hidden enemies' chips are dimmed so a glance at the top bar matches the map.
 */
export function updateChipVisibility(
  state: GameState,
  viewerIndex: number,
  visibleEnemyIds: Set<number>,
  fog: { ghosts: Array<{ playerId: number; turnSeen: number }> } | undefined,
  fogCam: boolean,
): void {
  const viewer = state.players[viewerIndex];
  for (const p of state.players) {
    const tag = document.getElementById(`pvis-${p.id}`);
    const bar = document.getElementById(`pbar-${p.id}`);
    if (!tag || !bar) continue;
    const isEnemy = !!viewer && p.teamIndex !== viewer.teamIndex;
    if (!isEnemy || !fogCam || !p.alive) {
      tag.textContent = '';
      tag.className = 'vis-tag';
      bar.classList.remove('hidden-enemy');
      continue;
    }
    if (visibleEnemyIds.has(p.id)) {
      tag.textContent = 'SEEN';
      tag.className = 'vis-tag vis-seen';
      bar.classList.remove('hidden-enemy');
      continue;
    }
    const ghost = fog?.ghosts.find(g => g.playerId === p.id);
    if (ghost) {
      const age = state.turnNumber - ghost.turnSeen;
      tag.textContent = age <= 0 ? 'T-0' : `T-${age}`;
      tag.className = 'vis-tag vis-ghost';
    } else {
      tag.textContent = '?';
      tag.className = 'vis-tag vis-unknown';
    }
    bar.classList.add('hidden-enemy');
  }
}

// =============================================================================
// THREAT EDGE MARKERS — a threatened unit that is off-screen gets a chevron on the screen edge
// =============================================================================

export interface ThreatEdgeEntry {
  id: number;
  /** Normalised device coordinates of the unit (x, y in [-1, 1] when on screen); `behind` when behind the camera. */
  ndcX: number;
  ndcY: number;
  behind: boolean;
}

const EDGE_MARGIN = 28;

export function updateThreatEdgeMarkers(entries: ThreatEdgeEntry[]): void {
  const hud = document.getElementById('hud');
  if (!hud) return;
  const live = new Set<string>();
  for (const e of entries) {
    const id = `threat-edge-${e.id}`;
    live.add(id);
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.className = 'threat-edge';
      el.textContent = '▲';
      hud.appendChild(el);
    }
    const onScreen = !e.behind && Math.abs(e.ndcX) <= 1 && Math.abs(e.ndcY) <= 1;
    if (onScreen) { el.style.display = 'none'; continue; }
    // Direction to the unit on screen; behind the camera, flip so the marker points "around"
    let dx = e.ndcX, dy = e.ndcY;
    if (e.behind) { dx = -dx; dy = -dy; }
    const len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;
    const w = window.innerWidth, h = window.innerHeight;
    // Project the direction onto the screen rectangle (minus margin)
    const hx = w / 2 - EDGE_MARGIN, hy = h / 2 - EDGE_MARGIN;
    const k = Math.min(hx / Math.abs(dx || 1e-6), hy / Math.abs(dy || 1e-6));
    const sx = w / 2 + dx * k;
    const sy = h / 2 - dy * k;
    const angle = Math.atan2(-dy, dx) * 180 / Math.PI + 90; // ▲ points up at 0°
    el.style.display = '';
    el.style.transform = `translate(${sx - 12}px, ${sy - 12}px) rotate(${angle}deg)`;
  }
  for (const el of Array.from(hud.querySelectorAll<HTMLElement>('.threat-edge'))) {
    if (!live.has(el.id)) el.remove();
  }
}

// =============================================================================
// TURN INFO
// =============================================================================

export function updateTurnInfo(state: GameState): void {
  const el = document.getElementById('turn-info');
  if (!el) return;

  const player = state.players[state.currentPlayerIndex];
  const turnLimit = state.config.modeConfig?.turnLimit;
  const turnText = turnLimit
    ? `Turn ${state.turnNumber} / ${turnLimit}`
    : `Turn ${state.turnNumber}`;

  const modeState = state.modeState;

  // Proximity to interactable structures
  let proximityText = '';
  if ((modeState.signalTrace || modeState.uplink || modeState.excavation)
    && !player.isBot && player.alive) {
    if (modeState.excavation) {
      // Show distance to artifact site
      if (player.planetIndex === modeState.excavation.artifactPlanetIndex) {
        const d = modeState.excavation.artifactSurfaceDir;
        const p = player.surfaceDir;
        const dot = Math.max(-1, Math.min(1, (d.x * p.x + d.y * p.y + d.z * p.z) /
          (Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) * Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z))));
        const planet = state.planets[player.planetIndex];
        const arcDist = Math.acos(dot) * (planet?.radius ?? 1);
        proximityText = arcDist <= 5.0
          ? ` | Artifact: IN RANGE`
          : ` | Artifact: ${arcDist.toFixed(1)} units`;
      }
    } else if (state.ruins.length > 0) {
      // Show distance to nearest interactable ruin on same planet
      let nearest = Infinity;
      for (const r of state.ruins) {
        if (!r.interactable || r.planetIndex !== player.planetIndex) continue;
        const d = r.surfaceDir;
        const p = player.surfaceDir;
        const dot = Math.max(-1, Math.min(1, (d.x * p.x + d.y * p.y + d.z * p.z) /
          (Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) * Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z))));
        const planet = state.planets[player.planetIndex];
        const arcDist = Math.acos(dot) * (planet?.radius ?? 1);
        if (arcDist < nearest) nearest = arcDist;
      }
      if (nearest < Infinity) {
        const INTERACT_RANGE = 2.0;
        proximityText = nearest <= INTERACT_RANGE
          ? ` | Ruin: IN RANGE`
          : ` | Ruin: ${nearest.toFixed(1)} units`;
      }
    }
  }

  // Active match event indicators
  let eventText = '';
  if (state.matchEvents) {
    const activeEvents = state.matchEvents.events.filter(e => e.active && !e.expired);
    if (activeEvents.length > 0) {
      const labels = activeEvents.map(e => {
        switch (e.type) {
          case 'solarFlare': return 'SOLAR FLARE';
          case 'asteroidPass': return 'ASTEROID';
          case 'commsBlackout': return 'COMMS OUT';
          case 'debrisField': return 'DEBRIS';
          default: return '';
        }
      }).filter(l => l.length > 0);
      if (labels.length > 0) {
        eventText = ` | ${labels.join(' / ')}`;
      }
    }
  }

  // Carried object indicator
  let carryText = '';
  if (state.carryables.length > 0) {
    const carried = state.carryables.find(c => c.alive && c.carriedBy === player.id);
    if (carried) {
      const typeLabels: Record<string, string> = {
        navCore: 'Enemy Nav Core',
        dataFragment: 'Data Fragment',
        salvageNode: 'Salvage',
        artifact: 'Artifact',
      };
      carryText = ` | Carrying: ${typeLabels[carried.type] ?? carried.type}`;
    }
  }

  const modeName = matchDisplayName(state.config.modeConfig);
  // 57.4: the stealth layer's cue — this planet's alert level, SEEN when its guards saw someone this turn, LOUD once the match went loud
  let alertText = '';
  const alert = modeState.alert;
  if (alert && player.alive) {
    const here = alert.byPlanet[player.planetIndex];
    if (here) {
      const seen = here.lastSeen?.turn === state.turnNumber ? ' · SEEN' : '';
      alertText = ` | ${here.level.toUpperCase()}${seen}${alert.wentLoud ? ' · LOUD' : ''}`;
    }
  }
  el.textContent = `${modeName} | ${turnText} — ${player.name}${carryText}${proximityText}${eventText}${alertText}`;

  // Update centered scoreboard
  updateScoreboard(state);
}

// =============================================================================
// SCOREBOARD (centered top)
// =============================================================================

interface TeamScoreEntry {
  teamIdx: number;
  name: string;
  color: string;
  score: string;
  detail?: string;
}

function getTeamScoreEntries(state: GameState): TeamScoreEntry[] {
  const modeState = state.modeState;
  const allTeams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const entries: TeamScoreEntry[] = [];

  const teamName = (idx: number) => TEAMS[idx % TEAMS.length].name.split(' ')[0];
  const teamColor = (idx: number) => TEAMS[idx % TEAMS.length].primary;

  if (modeState.territory) {
    const { territoryScores } = modeState.territory;
    for (const t of allTeams) {
      entries.push({
        teamIdx: t, name: teamName(t), color: teamColor(t),
        score: `${territoryScores[t] ?? 0}`,
        detail: 'territory',
      });
    }
  } else if (modeState.ctf) {
    const ctf = modeState.ctf;
    const capturesReq = state.config.modeConfig.capturesRequired ?? 3;
    for (const t of allTeams) {
      const caps = ctf.scores[t] ?? 0;
      const flagSt = ctf.flagStatus[t] ?? 'base';
      const flagIcon = flagSt === 'base' ? 'BASE' : flagSt === 'carried' ? 'CARRIED' : 'DROPPED';
      const tickets = modeState.respawn?.tickets[t] ?? 0;
      entries.push({
        teamIdx: t, name: teamName(t), color: teamColor(t),
        score: `${caps}/${capturesReq}`,
        detail: `${flagIcon} · ${tickets} tix`,
      });
    }
  } else if (modeState.signalTrace) {
    const st = modeState.signalTrace;
    const postsReq = state.config.modeConfig.postsToTriangulate ?? 3;
    for (const t of allTeams) {
      const posts = (st.clueState.activatedPosts[t] ?? []).length;
      const revealed = st.clueState.signalRevealed[t] ? ' REVEALED' : '';
      entries.push({
        teamIdx: t, name: teamName(t), color: teamColor(t),
        score: `${posts}/${postsReq}`,
        detail: `posts${revealed}`,
      });
    }
    if (st.extraction) {
      // Add extraction status as a special entry
      entries.push({
        teamIdx: -1, name: 'EXTRACT', color: theme.indicator,
        score: `${st.extraction.turnsHeld}/${st.extraction.turnsRequired}`,
      });
    }
  } else if (modeState.uplink) {
    const fragReq = state.config.modeConfig.fragmentsRequired ?? 4;
    for (const t of allTeams) {
      const frags = (modeState.uplink.uploadedFragments[t] ?? []).length;
      const teamPlayers = state.players.filter(p => p.teamIndex === t);
      const carrying = state.carryables.filter(
        c => c.type === 'dataFragment' && c.carriedBy !== null &&
        teamPlayers.some(p => p.id === c.carriedBy)
      ).length;
      entries.push({
        teamIdx: t, name: teamName(t), color: teamColor(t),
        score: `${frags}/${fragReq}`,
        detail: carrying > 0 ? `+${carrying} carrying` : 'fragments',
      });
    }
  } else if (modeState.salvage) {
    for (const t of allTeams) {
      const value = modeState.salvage.deliveredValue[t] ?? 0;
      entries.push({
        teamIdx: t, name: teamName(t), color: teamColor(t),
        score: `${value}`,
        detail: 'salvage pts',
      });
    }
  } else if (modeState.excavation) {
    const drills = modeState.excavation.drillSites;
    if (drills.length > 0) {
      const drill = drills[drills.length - 1];
      let status = '';
      if (!drill.built) status = `Build ${drill.buildTurnsRemaining}`;
      else if (drill.operating) status = `Op ${drill.operationTurnsRemaining}`;
      else if (drill.extracted) status = 'EXTRACTED';
      if (status) {
        entries.push({
          teamIdx: -1, name: 'DRILL', color: theme.indicator,
          score: status,
        });
      }
    }
  }

  return entries;
}

export function clearScoreboard(): void {
  const el = document.getElementById('scoreboard');
  if (el) {
    el.style.display = 'none';
    el.innerHTML = '';
  }
}

function updateScoreboard(state: GameState): void {
  const el = document.getElementById('scoreboard');
  if (!el) return;

  const entries = getTeamScoreEntries(state);

  if (entries.length === 0) {
    el.style.display = 'none';
    return;
  }

  el.style.display = 'flex';

  // Rebuild only if team count changed (avoid DOM thrashing)
  const childCount = el.children.length;
  if (childCount !== entries.length) {
    el.innerHTML = '';
    for (const entry of entries) {
      const card = document.createElement('div');
      card.className = 'sb-team';
      card.style.borderColor = entry.color + '40';
      card.innerHTML = `<div class="sb-team-name" style="color:${entry.color}">${entry.name}</div>`
        + `<div class="sb-team-score" style="color:${entry.color}">${entry.score}</div>`
        + (entry.detail ? `<div class="sb-team-detail">${entry.detail}</div>` : '');
      el.appendChild(card);
    }
  } else {
    // Update in place
    for (let i = 0; i < entries.length; i++) {
      const card = el.children[i] as HTMLElement;
      const entry = entries[i];
      card.style.borderColor = entry.color + '40';
      const nameEl = card.querySelector('.sb-team-name') as HTMLElement;
      const scoreEl = card.querySelector('.sb-team-score') as HTMLElement;
      const detailEl = card.querySelector('.sb-team-detail') as HTMLElement | null;
      if (nameEl) { nameEl.textContent = entry.name; nameEl.style.color = entry.color; }
      if (scoreEl) { scoreEl.textContent = entry.score; scoreEl.style.color = entry.color; }
      if (detailEl && entry.detail) { detailEl.textContent = entry.detail; }
    }
  }
}

// =============================================================================
// CONTROLS BAR
// =============================================================================

export function showControlsBar(show: boolean): void {
  const bar = document.getElementById('controls-bar');
  if (bar) {
    bar.classList.toggle('hidden', !show);
  }
}

// =============================================================================
// AIM SPHERE WIDGET
// =============================================================================

let aimSphereCtx: CanvasRenderingContext2D | null | undefined;

export function updateAimSphere(azimuth: number, elevation: number, power: number): void {
  if (aimSphereCtx === undefined) {
    const canvas = document.getElementById('aim-sphere') as HTMLCanvasElement | null;
    aimSphereCtx = canvas ? canvas.getContext('2d') : null;
  }
  const ctx = aimSphereCtx;
  if (!ctx) return;

  const W = 72;
  const cx = W / 2;
  const cy = W / 2;
  const R = 28; // planet radius in pixels
  const TILT = 25 * Math.PI / 180; // view tilt angle
  const cosTilt = Math.cos(TILT);
  const sinTilt = Math.sin(TILT);

  ctx.clearRect(0, 0, W, W);

  // Dark planet circle
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = theme.aimSphere.planetFill;
  ctx.fill();

  // Equator ellipse (3D depth cue)
  const eMinor = R * Math.sin(TILT); // ~12px
  ctx.beginPath();
  ctx.ellipse(cx, cy, R, eMinor, 0, 0, Math.PI * 2);
  ctx.strokeStyle = theme.aimSphere.equator;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Front tick at azimuth=0 on the equator
  // azimuth=0 maps to x=1,y=0 in local frame → screen: sx=0, sy=sinTilt
  const tickSx = cx;
  const tickSy = cy + R * sinTilt;
  ctx.beginPath();
  ctx.arc(tickSx, tickSy, 2, 0, Math.PI * 2);
  ctx.fillStyle = theme.aimSphere.frontTick;
  ctx.fill();

  // Aim direction in player local frame
  const cosEl = Math.cos(elevation);
  const sinEl = Math.sin(elevation);
  const cosAz = Math.cos(azimuth);
  const sinAz = Math.sin(azimuth);
  const lx = cosEl * cosAz;  // forward tangent
  const ly = cosEl * sinAz;  // sideways tangent
  const lz = sinEl;          // up (normal)

  // 3/4 view projection
  const sx = ly;
  const sy = -(lz * cosTilt - lx * sinTilt);

  // Line length scales with power (18–30px)
  const lineLen = 18 + (power / 100) * 12;

  const tipX = cx + sx * lineLen;
  const tipY = cy + sy * lineLen;

  // Aim line
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(tipX, tipY);
  ctx.strokeStyle = theme.aimSphere.aimLine;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Tip dot
  ctx.beginPath();
  ctx.arc(tipX, tipY, 3, 0, Math.PI * 2);
  ctx.fillStyle = theme.aimSphere.tipDot;
  ctx.fill();
}

export function updateAimDisplay(azimuth: number, elevation: number, power: number): void {
  const azEl = document.getElementById('val-azimuth');
  const elEl = document.getElementById('val-elevation');
  const pwEl = document.getElementById('val-power');
  const slider = document.getElementById('power-slider') as HTMLInputElement | null;

  if (azEl) {
    azEl.textContent = `${Math.round((azimuth * 180) / Math.PI)}°`;
  }
  if (elEl) {
    elEl.textContent = `${Math.round((elevation * 180) / Math.PI)}°`;
  }
  if (pwEl) {
    pwEl.textContent = `${Math.round(power)}%`;
  }
  if (slider) {
    slider.value = String(power);
  }
}

// =============================================================================
// GAME LOG
// =============================================================================

// Build weapon highlight lookup table once
const _weaponHighlights: Array<{ pattern: RegExp; color: string }> = [];
for (const wDef of Object.values(WEAPONS)) {
  _weaponHighlights.push({
    pattern: new RegExp(`\\b${wDef.name}\\b`, 'g'),
    color: wDef.color,
  });
}

// Player name highlights — rebuilt when players change (game start, respawn)
let _playerHighlights: Array<{ pattern: RegExp; color: string }> = [];

/**
 * Register player names and their team colors for log highlighting.
 * Called from buildPlayerBars so highlights match actual in-game names.
 */
export function setPlayerHighlights(players: PlayerState[]): void {
  _playerHighlights = [];
  // Sort by name length descending so longer names match first
  const sorted = [...players].sort((a, b) => b.name.length - a.name.length);
  const seen = new Set<string>();
  for (const p of sorted) {
    if (seen.has(p.name)) continue;
    seen.add(p.name);
    const team = TEAMS[p.teamIndex % TEAMS.length];
    // Escape for use in HTML (after escapeHtml runs on the message)
    const escapedName = p.name
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    _playerHighlights.push({
      pattern: new RegExp(escapedName, 'g'),
      color: team.primary,
    });
  }
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function highlightLogText(msg: string): string {
  let html = escapeHtml(msg);

  // Highlight player names (matches actual in-game names, longest first)
  for (const { pattern, color } of _playerHighlights) {
    html = html.replace(pattern, match =>
      `<span style="color:${color};font-weight:bold">${match}</span>`
    );
  }

  // Highlight weapon names
  for (const { pattern, color } of _weaponHighlights) {
    html = html.replace(pattern, match =>
      `<span style="color:${color};font-weight:bold">${match}</span>`
    );
  }

  return html;
}

export function logMessage(msg: string): void {
  const log = document.getElementById('game-log');
  if (!log) return;

  const e = document.createElement('div');
  e.className = 'log-entry';
  e.innerHTML = highlightLogText(msg);
  log.appendChild(e);
  log.scrollTop = log.scrollHeight;

  // Keep log from growing too large
  if (log.children.length > 30) {
    log.removeChild(log.firstChild!);
  }
}

export function clearLog(): void {
  const log = document.getElementById('game-log');
  if (log) {
    log.innerHTML = '';
  }
}

// =============================================================================
// GAME OVER
// =============================================================================

export function showGameOver(
  result: WinResult | null,
  turnNumber: number,
  players?: PlayerState[],
  modeConfig?: ModeConfig,
  shotRecords?: ShotRecord[],
): void {
  const objective = getModeRules(modeConfig).objective;
  const shotStats = summarizeShots(shotRecords ?? []);
  const bestShot = shotOfTheMatch(shotRecords ?? []);
  const overlay = document.getElementById('game-over-overlay');
  const textEl = document.getElementById('game-over-text');
  const subEl = document.getElementById('game-over-sub');
  const reportEl = document.getElementById('game-over-report');

  if (!overlay || !textEl || !subEl) return;

  const winner = result?.winningPlayer ?? null;

  if (winner) {
    const color = TEAMS[winner.teamIndex % TEAMS.length].primary;
    // Show company name (team) when multi-unit, otherwise individual name
    const hasMultiUnit = players ? players.some(p => p.unitIndex > 0) : false;
    const displayName = hasMultiUnit ? winner.companyName : winner.name;
    textEl.textContent = `${displayName} Wins!`;
    textEl.style.color = color;
  } else {
    textEl.textContent = 'Draw!';
    textEl.style.color = theme.text.primary;
  }

  // Win reason subtitle
  const isConquest = objective === 'stakes';
  const isCTF = objective === 'ctf';
  const hasRespawn = getModeRules(modeConfig).respawn;
  const reasonLabels: Record<string, string> = {
    elimination: hasRespawn ? 'All Enemy Tickets Spent' : 'Last Team Standing',
    turnLimit: isConquest ? 'Turn Limit — Most Territory'
      : isCTF ? 'Turn Limit — Most Captures'
      : objective === 'salvage' ? 'Turn Limit — Highest Salvage Value'
      : objective === 'signalTrace' ? 'Turn Limit — Most Posts Activated'
      : objective === 'uplink' ? 'Turn Limit — Most Fragments Uploaded'
      : objective === 'excavation' ? 'Turn Limit — Drill Progress'
      : 'Turn Limit — Most Survivors',
    objective: isCTF ? 'Captures Complete'
      : objective === 'signalTrace' ? 'Signal Extracted'
      : objective === 'uplink' ? 'Data Upload Complete'
      : objective === 'salvage' ? 'Salvage Quota Reached'
      : objective === 'excavation' ? 'Artifact Secured'
      : 'Objective Complete',
  };
  const reasonText = result ? reasonLabels[result.reason] ?? '' : '';
  const subText = reasonText
    ? `${reasonText} — Game lasted ${turnNumber} turns`
    : `Game lasted ${turnNumber} turns`;
  subEl.textContent = subText;

  // Build stats report
  if (reportEl && players && players.length > 0) {
    // Group by team, then sort teams by total damage dealt
    const teamMap = new Map<number, PlayerState[]>();
    for (const p of players) {
      const arr = teamMap.get(p.teamIndex) || [];
      arr.push(p);
      teamMap.set(p.teamIndex, arr);
    }
    const teams = [...teamMap.entries()].sort((a, b) => {
      const aDmg = a[1].reduce((s, p) => s + p.damageDealt, 0);
      const bDmg = b[1].reduce((s, p) => s + p.damageDealt, 0);
      return bDmg - aDmg;
    });

    let html = '';

    // Territory breakdown for conquest modes
    if (isConquest && result?.teamScores) {
      html += '<div class="report-header">TERRITORY</div>';
      html += '<div class="report-table">';
      for (const [teamIdx] of teams) {
        const teamColor = TEAMS[teamIdx % TEAMS.length].primary;
        const teamName = TEAMS[teamIdx % TEAMS.length].name;
        const score = result.teamScores[teamIdx] ?? 0;
        const planets = Math.floor(score / 1000);
        const stakes = score % 1000;
        html += `
          <div class="report-row">
            <span class="report-name" style="color:${teamColor}">${teamName}</span>
            <span class="report-stat">${planets} planet${planets !== 1 ? 's' : ''}</span>
            <span class="report-stat">${stakes} stake${stakes !== 1 ? 's' : ''}</span>
          </div>
        `;
      }
      html += '</div>';
    }

    // CTF capture breakdown
    if (isCTF && result?.teamScores) {
      html += '<div class="report-header">CAPTURES</div>';
      html += '<div class="report-table">';
      for (const [teamIdx] of teams) {
        const teamColor = TEAMS[teamIdx % TEAMS.length].primary;
        const teamName = TEAMS[teamIdx % TEAMS.length].name;
        const captures = result.teamScores[teamIdx] ?? 0;
        html += `
          <div class="report-row">
            <span class="report-name" style="color:${teamColor}">${teamName}</span>
            <span class="report-stat">${captures} capture${captures !== 1 ? 's' : ''}</span>
          </div>
        `;
      }
      html += '</div>';
    }

    // Salvage Run value breakdown
    if (objective === 'salvage' && result?.teamScores) {
      html += '<div class="report-header">SALVAGE DELIVERED</div>';
      html += '<div class="report-table">';
      for (const [teamIdx] of teams) {
        const teamColor = TEAMS[teamIdx % TEAMS.length].primary;
        const teamName = TEAMS[teamIdx % TEAMS.length].name;
        const value = result.teamScores[teamIdx] ?? 0;
        html += `
          <div class="report-row">
            <span class="report-name" style="color:${teamColor}">${teamName}</span>
            <span class="report-stat">${value} pts</span>
          </div>
        `;
      }
      html += '</div>';
    }

    // Uplink fragment breakdown
    if (objective === 'uplink' && result?.teamScores) {
      html += '<div class="report-header">DATA FRAGMENTS</div>';
      html += '<div class="report-table">';
      for (const [teamIdx] of teams) {
        const teamColor = TEAMS[teamIdx % TEAMS.length].primary;
        const teamName = TEAMS[teamIdx % TEAMS.length].name;
        const score = result.teamScores[teamIdx] ?? 0;
        const frags = Math.floor(score / 1000);
        html += `
          <div class="report-row">
            <span class="report-name" style="color:${teamColor}">${teamName}</span>
            <span class="report-stat">${frags} uploaded</span>
          </div>
        `;
      }
      html += '</div>';
    }

    if (bestShot && players) {
      const shooter = players.find(p => p.id === bestShot.shooterId);
      if (shooter) {
        const color = TEAMS[shooter.teamIndex % TEAMS.length].primary;
        html += '<div class="report-header">SHOT OF THE MATCH</div>';
        html += `<div class="report-row"><span class="report-name" style="color:${color}">${shooter.name}</span>`
          + `<span class="report-stat" style="flex:3;text-align:right;font-size:10px;">${describeShot(bestShot, PHYS_DT).replace('Shot report: ', '')}</span></div>`;
      }
    }

    html += '<div class="report-header">FINAL STATS</div>';
    html += '<div class="report-table">';

    for (const [teamIdx, teamPlayers] of teams) {
      const teamColor = TEAMS[teamIdx % TEAMS.length].primary;
      // Team header if multiple players on a team
      if (teamPlayers.length > 1) {
        const teamDmg = teamPlayers.reduce((s, p) => s + p.damageDealt, 0);
        const teamKills = teamPlayers.reduce((s, p) => s + p.kills, 0);
        html += `
          <div class="report-row" style="opacity:0.6;border-bottom:1px solid ${teamColor}33;margin-top:4px;">
            <span class="report-name" style="color:${teamColor};font-size:10px;">${teamPlayers[0].companyName} (Team)</span>
            <span class="report-stat"></span>
            <span class="report-stat" style="font-size:10px;">${Math.round(teamDmg)} dmg</span>
            <span class="report-stat" style="font-size:10px;">${teamKills} kill${teamKills !== 1 ? 's' : ''}</span>
            <span class="report-stat"></span>
          </div>
        `;
      }
      for (const p of teamPlayers) {
        const color = TEAMS[p.teamIndex % TEAMS.length].primary;
        const status = p.alive ? '✓' : '✗';
        const statusColor = p.alive ? theme.positive : theme.negative;

        html += `
          <div class="report-row"${teamPlayers.length > 1 ? ' style="padding-left:8px;"' : ''}>
            <span class="report-name" style="color:${color}">${p.name}${p.isBot ? ' 🤖' : ''}</span>
            <span class="report-stat" style="color:${statusColor}">${status}</span>
            <span class="report-stat">${Math.round(p.damageDealt)} dmg</span>
            <span class="report-stat">${p.kills} kill${p.kills !== 1 ? 's' : ''}</span>
            <span class="report-stat">${p.shotsFired} shot${p.shotsFired !== 1 ? 's' : ''}${(shotStats.get(p.id)?.assists ?? 0) > 0 ? ` · ${shotStats.get(p.id)!.assists} assist${shotStats.get(p.id)!.assists !== 1 ? 's' : ''}` : ''}</span>
          </div>
        `;
      }
    }

    html += '</div>';
    reportEl.innerHTML = html;
    reportEl.classList.remove('hidden');
  }

  overlay.classList.remove('hidden');
  showControlsBar(false);
}

export function hideGameOver(): void {
  const overlay = document.getElementById('game-over-overlay');
  const reportEl = document.getElementById('game-over-report');
  if (overlay) {
    overlay.classList.add('hidden');
  }
  if (reportEl) {
    reportEl.classList.add('hidden');
    reportEl.innerHTML = '';
  }
}

// =============================================================================
// MENU
// =============================================================================

export function showMenu(show: boolean): void {
  const menu = document.getElementById('menu-overlay');
  if (menu) {
    menu.classList.toggle('hidden', !show);
  }
}

// =============================================================================
// CAMERA MODE INDICATOR
// =============================================================================

/** The visibility chip: what the viewer can see this match (and the dev reveal when on). */
export function updateCameraMode(visibility: 'fog' | 'free', reveal: boolean): void {
  const el = document.getElementById('camera-mode');
  if (!el) return;
  const mode = reveal ? 'sissi' : visibility;
  const labels: Record<string, string> = { fog: 'FOG OF WAR', free: 'ALL VISIBLE', sissi: 'SISSI (V)' };
  const borderColors: Record<string, string> = {
    fog: theme.camera.freeBorder,
    free: theme.camera.orbitBorder,
    sissi: theme.camera.fpBorder,
  };
  const textColors: Record<string, string> = { fog: theme.camera.freeText, free: theme.camera.orbitText, sissi: theme.camera.fpText };
  el.textContent = labels[mode];
  el.style.borderColor = borderColors[mode];
  el.style.color = textColors[mode];
  el.classList.toggle('dev-only', false);
  el.style.cursor = isDevMode() ? 'pointer' : 'default';
}

// =============================================================================
// IN-GAME HELP & SETTINGS
// =============================================================================

export function showInGameHelp(show: boolean, mode?: GameMode): void {
  const el = document.getElementById('ingame-help-overlay');
  if (el) el.classList.toggle('hidden', !show);
  if (show && mode) updateModeRulesInHelp(mode);
}

export function updateModeRulesInHelp(mode: GameMode): void {
  const el = document.getElementById('mode-rules-ingame');
  if (!el) return;
  const info = MODE_RULES[mode];
  if (!info) { el.innerHTML = ''; return; }
  const bullets = info.rules.map(r => `&bull; ${r}`).join('<br>');
  el.innerHTML = `<strong>${fx('Objective', '\u00A73.1 Mission Briefing')}</strong><br>`
    + `<em>${info.objective}</em><br>${bullets}`;
}

export function isInGameHelpVisible(): boolean {
  const el = document.getElementById('ingame-help-overlay');
  return el ? !el.classList.contains('hidden') : false;
}

export function showInGameSettings(show: boolean): void {
  const el = document.getElementById('ingame-settings-overlay');
  if (el) el.classList.toggle('hidden', !show);
}

export function isInGameSettingsVisible(): boolean {
  const el = document.getElementById('ingame-settings-overlay');
  return el ? !el.classList.contains('hidden') : false;
}

export function updateSettingsToggles(visibility: 'fog' | 'free', reveal: boolean, trailPersist: boolean, trailHistoryRounds?: number): void {
  const visBtn = document.getElementById('setting-visibility');
  const revealBtn = document.getElementById('setting-sissi');
  const trailBtn = document.getElementById('setting-trail-persist');
  if (visBtn) {
    visBtn.textContent = visibility === 'fog' ? 'FOG OF WAR' : 'ALL VISIBLE';
    visBtn.classList.toggle('off', visibility === 'free');
  }
  if (revealBtn) {
    revealBtn.textContent = reveal ? 'ON' : 'OFF';
    revealBtn.classList.toggle('off', !reveal);
  }
  if (trailBtn) {
    trailBtn.textContent = trailPersist ? 'ENABLED' : 'DISABLED';
    trailBtn.classList.toggle('off', !trailPersist);
  }
  if (trailHistoryRounds !== undefined) {
    const roundsLabel = document.getElementById('setting-trail-rounds-label');
    if (roundsLabel) roundsLabel.textContent = `${trailHistoryRounds}`;
  }
  updateFlavorToggles();
  updateDevToggles();
}

export function updateVolumeSliders(getVolume: (bus: string) => number): void {
  const buses = ['master', 'sfx', 'music', 'ambient', 'ui'];
  for (const bus of buses) {
    const vol = getVolume(bus);
    const slider = document.getElementById(`setting-vol-${bus}`) as HTMLInputElement | null;
    const label = document.getElementById(`setting-vol-${bus}-label`);
    if (slider) slider.value = String(Math.round(vol * 100));
    if (label) label.textContent = `${Math.round(vol * 100)}%`;
  }
}

// =============================================================================
// INCIDENT REPORT (ERROR SCREEN)
// =============================================================================

/** Classification labels mapped from error context. */
const INCIDENT_CLASSIFICATIONS: Record<string, string> = {
  render: 'Telemetry Visualization Failure',
  network: 'Inter-Contractor Communication Disruption',
  disconnect: 'Telemetry Link Severed',
  physics: 'Orbital Mechanics Anomaly',
  state: 'Operational State Corruption',
  webrtc: 'Secure Channel Handshake Failure',
  generic: 'Unclassified Operational Anomaly',
};

const INCIDENT_BODIES: Record<string, string> = {
  render: 'The Conservation Zone Assessment visualization system has encountered an irrecoverable instrument calibration failure. Your session data has been preserved where possible.',
  network: 'Communication with one or more participating contractors has been interrupted. The Council reminds all parties that signal degradation in contested sectors is expected and not grounds for contract extension.',
  disconnect: 'Your telemetry link to the Conservation Zone has been severed. This may be due to solar interference, budget cuts to relay infrastructure, or contractor-initiated signal disruption.',
  physics: 'The orbital mechanics computation engine has encountered a state outside validated parameters. The Council notes that conservation zones with "unusual gravitational profiles" are not covered under standard liability.',
  state: 'An internal data inconsistency has been detected in the Assessment Management System. Your quarterly review cycle has been suspended pending data recovery.',
  webrtc: 'The encrypted contractor communication channel could not be established. Please verify your equipment certification is current (Form 7-C).',
  generic: 'An anomalous event has been detected during Conservation Zone Assessment operations. The Council has been notified. No further action is required on your part.',
};

const PLAIN_CLASSIFICATIONS: Record<string, string> = {
  render: 'Rendering error',
  network: 'Network error',
  disconnect: 'Connection lost',
  physics: 'Physics error',
  state: 'Game state error',
  webrtc: 'Could not connect',
  generic: 'Unexpected error',
};

const PLAIN_BODIES: Record<string, string> = {
  render: 'The renderer hit an error it could not recover from.',
  network: 'Contact with another player was interrupted.',
  disconnect: 'The connection to the other player dropped.',
  physics: 'The physics simulation produced an invalid state.',
  state: 'The game state became inconsistent.',
  webrtc: 'The peer-to-peer connection could not be established.',
  generic: 'Something went wrong during the match.',
};

function generateReportNumber(): string {
  const hex = () => Math.floor(Math.random() * 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
  const seq = Math.floor(Math.random() * 9000) + 1000;
  return `IR-${hex()}-${hex()}-${seq}`;
}

export function showErrorScreen(error: string | Error, context: string = 'generic'): void {
  const overlay = document.getElementById('error-overlay');
  if (!overlay) return;

  const key = context in INCIDENT_CLASSIFICATIONS ? context : 'generic';
  const flavor = isFlavorEnabled();
  const classification = (flavor ? INCIDENT_CLASSIFICATIONS : PLAIN_CLASSIFICATIONS)[key];
  const body = (flavor ? INCIDENT_BODIES : PLAIN_BODIES)[key];
  const reportNo = generateReportNumber();
  const errorStr = error instanceof Error ? error.message : String(error);

  const reportNoEl = document.getElementById('incident-report-no');
  const classEl = document.getElementById('incident-classification');
  const bodyEl = document.getElementById('incident-body');
  const detailsEl = document.getElementById('incident-details');

  if (reportNoEl) reportNoEl.textContent = flavor ? `Report No. ${reportNo}` : '';
  if (classEl) classEl.textContent = classification;
  if (bodyEl) bodyEl.textContent = body;
  if (detailsEl) detailsEl.textContent = `ref: ${errorStr}`;

  overlay.classList.remove('hidden');
}

export function hideErrorScreen(): void {
  const overlay = document.getElementById('error-overlay');
  if (overlay) overlay.classList.add('hidden');
}

/** Settings panel: flight resolution pace (Phase 40.1). */
export function updatePaceToggle(pace: ResolutionPace): void {
  const btn = document.getElementById('setting-resolution');
  if (!btn) return;
  btn.textContent = PACE_LABELS[pace];
  btn.classList.toggle('off', pace === 'full');
}
